import debounce from 'lodash/debounce';
import React, { useMemo, useState } from 'react';

import { DSErrorIcon } from '@ds-icons';
import { usePanelLibraryTemplateCode, usePythonPanelURL } from '@mpm-druid/api';
import { PanelExtraOptions, PythonPanelConfig } from '@mpm-druid/types';
import { useObserveResizeNode } from '@shared/hooks';

import styles from './Panel.module.scss';

const RESIZE_DEBOUNCE_TIME = 1_000;

type PythonPanelProps = {
  config: PythonPanelConfig;
  id: string;
  extraOptions: PanelExtraOptions;
};

export function PythonPanel({ config, id, extraOptions }: PythonPanelProps) {
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const {
    data: templateCode,
    isError: isTemplateError
  } = usePanelLibraryTemplateCode(
    {
      revisionId: config.panelLibraryRevisionId,
      templateId: config.panelLibraryId
    },
    { keepPreviousData: true }
  );
  const { data: panelURL } = usePythonPanelURL(
    {
      code: config.panelLibraryId ? templateCode : config.code,
      instanceId: id,
      options: { ...extraOptions },
      panelHeight: size?.height,
      panelWidth: size?.width
    },
    { keepPreviousData: true }
  );

  const handleDebouncedResize = useMemo(
    () =>
      debounce((newSize: { width: number; height: number }) => {
        setSize(newSize);
      }, RESIZE_DEBOUNCE_TIME),
    []
  );

  const onResizeRef = useObserveResizeNode(node => {
    if (node) {
      const { width, height } = node.getBoundingClientRect();
      handleDebouncedResize({ width, height });
    }
  });

  if (config.panelLibraryId && isTemplateError) {
    return (
      <div className={styles.panelWarning} data-test="python-panel-warning">
        <DSErrorIcon />
        Panel library template not found
      </div>
    );
  }

  if (!panelURL) {
    return <div ref={onResizeRef} style={{ width: '100%', height: '100%' }} />;
  }

  return (
    <iframe
      ref={onResizeRef}
      loading="lazy"
      /* TODO: remove allow-same-origin (it is unsafe) when the Streamlit supports it */
      sandbox="allow-same-origin allow-scripts allow-downloads allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
      src={panelURL}
      width="100%"
      height="100%"
    />
  );
}
